import { ScheduleEntry, StaffScheduleViewModel } from '@lumora/core';
import { useViewModel } from '@lumora/ui';
import { useEffect, useMemo, useState } from 'react';
import { SITE, client } from '../../client';
import type Car from '../../models/Car';

type AssociateAppointment = ScheduleEntry;

interface AssociateScheduleViewProps {
    inventory: Car[];
    onOpenCar: (car: Car) => void;
    isAdmin: boolean;
    focusAppointment?: { id: string; startTime?: string } | null;
}

const getWeekStart = (value: Date = new Date()) => {
    const date = new Date(value);
    const day = date.getDay();
    const diff = day === 0 ? -6 : 1 - day;
    date.setDate(date.getDate() + diff);
    date.setHours(0, 0, 0, 0);
    return date;
};

const formatDay = (date: Date) => new Intl.DateTimeFormat('en-US', { weekday: 'short', month: 'short', day: 'numeric' }).format(date);
const dateKey = (date: Date) => [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, '0'),
    String(date.getDate()).padStart(2, '0')
].join('-');
const appointmentDateKey = (value: string) => {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value.slice(0, 10);
    return dateKey(date);
};
const formatTime = (value: string) => {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit' }).format(date);
};

const statusClasses: Record<ScheduleEntry['status'], string> = {
    accepted: 'border-blue-300 bg-blue-50 text-blue-950',
    requested: 'border-yellow-300 bg-yellow-50 text-yellow-950',
    cancelled: 'border-gray-200 bg-gray-100 text-gray-500',
    declined: 'border-gray-200 bg-gray-100 text-gray-500',
};

export default function AssociateScheduleView({ inventory, onOpenCar, isAdmin, focusAppointment }: AssociateScheduleViewProps) {
    const viewModel = useMemo(() => new StaffScheduleViewModel(client, SITE), []);
    const state = useViewModel(viewModel);
    const appointments = state.entries;
    const isLoading = state.loading;
    const error = state.error ?? '';
    const [selectedId, setSelectedId] = useState<string | null>(null);
    const selected = appointments.find(item => item.id === selectedId) ?? null;
    const [viewAll, setViewAll] = useState(isAdmin);
    const [weekStart, setWeekStart] = useState(() => getWeekStart());
    const days = Array.from({ length: 7 }, (_, index) => {
        const date = new Date(weekStart);
        date.setDate(weekStart.getDate() + index);
        return date;
    });

    const shiftWeek = (weeks: number) => {
        setWeekStart(current => {
            const next = new Date(current);
            next.setDate(current.getDate() + weeks * 7);
            return getWeekStart(next);
        });
    };

    useEffect(() => {
        const reload = () => void viewModel.load();
        reload();
        window.addEventListener('appointments-updated', reload);
        return () => window.removeEventListener('appointments-updated', reload);
    }, [viewModel]);

    useEffect(() => {
        if (!focusAppointment?.startTime) return;
        const targetDate = new Date(focusAppointment.startTime);
        if (Number.isNaN(targetDate.getTime())) return;
        setWeekStart(getWeekStart(targetDate));
        if (isAdmin) setViewAll(true);
        void viewModel.load();
    }, [focusAppointment?.id, focusAppointment?.startTime, isAdmin, viewModel]);

    useEffect(() => {
        if (!focusAppointment?.id || appointments.length === 0) return;
        const target = appointments.find(appointment => appointment.id === focusAppointment.id);
        if (target) setSelectedId(target.id);
    }, [appointments, focusAppointment?.id]);

    const updateStatus = async (appointment: AssociateAppointment, status: 'accepted' | 'declined') => {
        const ok = status === 'accepted' ? await viewModel.accept(appointment.id) : await viewModel.decline(appointment.id);
        if (ok) window.dispatchEvent(new CustomEvent('appointments-updated'));
    };

    const cancelAppointment = async (appointment: AssociateAppointment) => {
        if (await viewModel.cancel(appointment.id)) window.dispatchEvent(new CustomEvent('appointments-updated'));
    };

    const renderCard = (appointment: AssociateAppointment) => (
        <button
            key={appointment.id}
            onClick={() => setSelectedId(appointment.id)}
            className={`w-full rounded-2xl border p-3 text-left shadow-sm transition hover:-translate-y-0.5 ${statusClasses[appointment.status]}`}
        >
            <div className="flex items-start justify-between gap-2">
                <div>
                    <p className="text-[10px] font-black uppercase tracking-widest">{formatTime(appointment.start)} - {formatTime(appointment.end)}</p>
                    <p className="mt-1 text-sm font-black">{appointment.car.year} {appointment.car.make} {appointment.car.model}</p>
                    {isAdmin && (
                        <p className="mt-1 text-[10px] font-black uppercase tracking-widest opacity-70">
                            Staff: {'Unassigned'}
                        </p>
                    )}
                    <p className="mt-1 text-[10px] font-black uppercase tracking-widest opacity-70">{appointment.status}</p>
                </div>
                {appointment.status === 'requested' && (
                    <span className="flex gap-1">
                        <span
                            role="button"
                            tabIndex={0}
                            aria-label={`Accept ${appointment.car.year} ${appointment.car.make} ${appointment.car.model}`}
                            onClick={event => {
                                event.stopPropagation();
                                updateStatus(appointment, 'accepted');
                            }}
                            onKeyDown={event => {
                                if (event.key === 'Enter') updateStatus(appointment, 'accepted');
                            }}
                            className="rounded-full bg-green-600 px-2 py-1 text-xs font-black text-white"
                        >
                            ✓
                        </span>
                        <span
                            role="button"
                            tabIndex={0}
                            aria-label={`Decline ${appointment.car.year} ${appointment.car.make} ${appointment.car.model}`}
                            onClick={event => {
                                event.stopPropagation();
                                updateStatus(appointment, 'declined');
                            }}
                            onKeyDown={event => {
                                if (event.key === 'Enter') updateStatus(appointment, 'declined');
                            }}
                            className="rounded-full bg-red-600 px-2 py-1 text-xs font-black text-white"
                        >
                            X
                        </span>
                    </span>
                )}
            </div>
        </button>
    );

    return (
        <div className="mx-auto w-full max-w-7xl px-4 py-10">
            <div className="mb-8 flex flex-col justify-between gap-4 md:flex-row md:items-end">
                <div>
                    <p className="text-[10px] font-black uppercase tracking-[0.3em] text-red-600">{isAdmin ? 'Admin Portal' : 'Associate Workspace'}</p>
                    <h1 className="mt-2 text-4xl font-black italic uppercase tracking-tight text-gray-950">Weekly Calendar</h1>
                    <p className="mt-2 text-sm font-bold text-gray-500">Store-hours shell for accepted showings and unclaimed requests.</p>
                </div>
                <div className="flex items-center gap-4">
                    {isAdmin && (
                        <div className="flex bg-white p-1 rounded-xl shadow-sm ring-1 ring-gray-100">
                            <button onClick={() => setViewAll(false)} className={`px-4 py-2 text-[10px] font-black uppercase rounded-lg transition ${!viewAll ? 'bg-red-600 text-white shadow-md' : 'text-gray-400 hover:bg-gray-50'}`}>My Work</button>
                            <button onClick={() => setViewAll(true)} className={`px-4 py-2 text-[10px] font-black uppercase rounded-lg transition ${viewAll ? 'bg-red-600 text-white shadow-md' : 'text-gray-400 hover:bg-gray-50'}`}>All Staff</button>
                        </div>
                    )}
                    <div className="flex bg-white p-1 rounded-xl shadow-sm ring-1 ring-gray-100">
                        <button onClick={() => shiftWeek(-1)} className="px-3 py-2 text-[10px] font-black uppercase rounded-lg text-gray-500 hover:bg-gray-50">Prev</button>
                        <button onClick={() => setWeekStart(getWeekStart())} className="px-3 py-2 text-[10px] font-black uppercase rounded-lg text-gray-500 hover:bg-gray-50">Today</button>
                        <button onClick={() => shiftWeek(1)} className="px-3 py-2 text-[10px] font-black uppercase rounded-lg text-gray-500 hover:bg-gray-50">Next</button>
                    </div>
                    <div className="rounded-2xl bg-white px-4 py-3 text-xs font-black uppercase tracking-widest text-gray-500 shadow-sm ring-1 ring-gray-100">
                        Week Of {formatDay(weekStart)}
                    </div>
                </div>
            </div>

            {error && <p className="mb-4 rounded-xl bg-amber-50 p-3 text-sm font-bold text-amber-800">{error}</p>}
            {isLoading && <p className="mb-4 text-sm font-bold text-gray-500">Loading appointments...</p>}

            <div className="hidden grid-cols-7 gap-3 lg:grid">
                {days.map(day => {
                    const dayKey = dateKey(day);
                    const dayAppointments = appointments.filter(item => appointmentDateKey(item.start) === dayKey);
                    return (
                        <section key={dayKey} className="min-h-96 rounded-3xl bg-white p-3 shadow-sm ring-1 ring-gray-100">
                            <h2 className="mb-3 text-xs font-black uppercase tracking-widest text-gray-500">{formatDay(day)}</h2>
                            <div className="space-y-3">
                                {dayAppointments.length === 0 && <p className="rounded-2xl border border-dashed border-gray-200 p-3 text-xs font-bold text-gray-400">Open</p>}
                                {dayAppointments.map(renderCard)}
                            </div>
                        </section>
                    );
                })}
            </div>

            <section className="mt-6 rounded-3xl bg-white p-4 shadow-sm ring-1 ring-gray-100">
                <div className="mb-4 flex items-center justify-between">
                    <h2 className="text-sm font-black uppercase tracking-widest text-gray-950">Appointment List</h2>
                    <span className="text-[10px] font-black uppercase tracking-widest text-red-600 bg-red-50 px-3 py-1 rounded-full">{appointments.length} Total Records</span>
                </div>
                <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                    {appointments.length === 0 && (
                        <div className="md:col-span-2 xl:col-span-3 py-10 text-center border-2 border-dashed border-gray-100 rounded-2xl">
                            <p className="text-sm font-bold text-gray-400 italic">No appointments found for this period.</p>
                        </div>
                    )}
                    {appointments.map(renderCard)}
                </div>
            </section>

            {selected && (
                <div className="fixed inset-0 z-[110] flex items-center justify-center bg-gray-950/60 p-4 backdrop-blur-sm" onClick={() => setSelectedId(null)}>
                    <section className="w-full max-w-lg rounded-3xl bg-white p-6 shadow-2xl" onClick={event => event.stopPropagation()}>
                        <p className="text-[10px] font-black uppercase tracking-[0.25em] text-red-600">{selected.status}</p>
                        <h2 className="mt-2 text-2xl font-black text-gray-950">{selected.car.year} {selected.car.make} {selected.car.model}</h2>
                        <p className="mt-2 text-sm font-bold text-gray-500">{formatTime(selected.start)} - {formatTime(selected.end)}</p>
                        {isAdmin && <p className="mt-4 text-sm font-bold text-gray-700">Staff: {'Unassigned'}</p>}
                        {selected.phone && <p className="mt-4 text-sm font-bold text-gray-700">Guest phone: {selected.phone}</p>}
                        {selected.notes && <p className="mt-2 rounded-2xl bg-gray-50 p-3 text-sm text-gray-700">{selected.notes}</p>}
                        <div className="mt-6 flex flex-col gap-3 sm:flex-row">
                            {selected.isCancellable() && (
                                <button
                                    onClick={() => cancelAppointment(selected)}
                                    className="flex-1 rounded-xl bg-red-600 px-4 py-3 text-xs font-black uppercase tracking-widest text-white hover:bg-red-700"
                                >
                                    Cancel Appointment
                                </button>
                            )}
                            <button
                                onClick={() => {
                                    const car = inventory.find(item => item.id === selected.listingId);
                                    if (car) onOpenCar(car);
                                }}
                                className="flex-1 rounded-xl bg-gray-900 px-4 py-3 text-xs font-black uppercase tracking-widest text-white hover:bg-red-600"
                            >
                                Open Vehicle
                            </button>
                            <button onClick={() => setSelectedId(null)} className="flex-1 rounded-xl bg-gray-100 px-4 py-3 text-xs font-black uppercase tracking-widest text-gray-700">
                                Close
                            </button>
                        </div>
                    </section>
                </div>
            )}
        </div>
    );
}
